import { FC, memo, useCallback } from 'react';

import { setScrollToTime } from '@/apps/mwd-v2/state/actions/scrollToTime';
import useTooltipActions from '@/apps/mwd-v2/state/actions/useTooltipActions';
import useMwdTimescale from '@/apps/mwd-v2/state/selectors/useMwdTimescale';
import { WerklijnLegendRight } from '@/apps/mwd-v2/types/legends';
import { Group } from '@visx/group';

import styles from './LegendRight.module.css';

type LegendProps = {
  legendRight: WerklijnLegendRight;
  height: number;
  y: number;
};

const LegendRightItem: FC<LegendProps> = ({ legendRight, height, y }) => {
  const { setShowRightLegendTooltip, hideTooltipWithTimeout } = useTooltipActions();
  const { scale } = useMwdTimescale();

  const handleClick = useCallback(() => {
    const vertrekTijd = legendRight?.eersteDeeltrajectVolgendeDag[0]?.vertrekTijd;
    if (vertrekTijd) {
      const x = scale(vertrekTijd);
      setScrollToTime(x);
    }
  }, [legendRight, scale]);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      void setShowRightLegendTooltip(
        {
          x: event.clientX,
          y: event.clientY,
        },
        legendRight?.eersteDeeltrajectVolgendeDag[0],
      );
    },
    [setShowRightLegendTooltip, legendRight],
  );

  const handleMouseLeave = useCallback(() => hideTooltipWithTimeout(), [hideTooltipWithTimeout]);

  return (
    <Group
      onClick={handleClick}
      id={`legendRight-${legendRight?.werklijnId}`}
      data-testid={`legendRight-${legendRight?.werklijnId}`}
    >
      <Group
        data-testid={`legend-right-content-${legendRight?.werklijnId}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {legendRight.isLastFavorite && (
          <line
            x={0}
            y1={y + height - 25}
            x2={128}
            y2={y + height - 25}
            stroke='var(--tractie-color-border-emphasis)'
          />
        )}
        <text
          y={y - 2}
          x={20}
          className={
            legendRight?.eersteDeeltrajectVolgendeDag[0]?.plaatsConflictVan
              ? styles.legendRightContentStyleV2InConflict
              : styles.legendRightContentStyleV2
          }
        >
          {legendRight?.eersteDeeltrajectVolgendeDag[0]?.dienstregelpuntVan}
        </text>
        <text x={20} y={y + 16} className={styles.legendRightContentStyleV2}>
          {legendRight?.eersteDeeltrajectVolgendeDag[0]?.claimsoort !== null
            ? legendRight?.eersteDeeltrajectVolgendeDag[0]?.claimsoort
            : legendRight?.eersteDeeltrajectVolgendeDag[0]?.treinnummer}
          {legendRight?.eersteDeeltrajectVolgendeDag[0]?.inzetPositie &&
            `-${legendRight?.eersteDeeltrajectVolgendeDag[0]?.inzetPositie}`}
          {legendRight?.eersteDeeltrajectVolgendeDag[0]?.versterkt && ' +'}
          {legendRight?.eersteDeeltrajectVolgendeDag[0]?.opzending && ' ='}
        </text>
        <text
          y={y + 34}
          x={20}
          className={`${legendRight?.eersteDeeltrajectVolgendeDag[0]?.verkeerdMat ? styles.omloopVerkeerdMatV2 : styles.omloopDefaultStyle} ${
            legendRight?.eersteDeeltrajectVolgendeDag[0]?.manco ? styles.omloopMancoV2 : ''
          }`}
        >
          {legendRight?.eersteDeeltrajectVolgendeDag[0]?.dagAfkorting && (
            <tspan className={styles.omloopNextDayStyle}>
              {`${legendRight.eersteDeeltrajectVolgendeDag[0].dagAfkorting}: `}
            </tspan>
          )}
          {legendRight?.eersteDeeltrajectVolgendeDag[0]?.omloop}
        </text>
      </Group>
    </Group>
  );
};

export default memo(LegendRightItem);
